/* eslint-disable arrow-body-style */
import React from 'react';
import {
  Conversation,
  IAnswerItemText,
  IAnswerItemThink,
  IAnswerItemReference,
  IAnswerItemBiData,
  IAnswerItemError,
  IAnswerSensitive,
  IAnswerCostWarning,
  IAnswerItemEchartsOption,
  IAnswerItemDataDescription,
  IAnswerItemBqChart,
  IAnswerItemOpinionReportCard,
  IAnswerItemDatabaseQuery,
  IDatabaseQueryGroup,
} from '../types/history';
import { v4 as uuidv4 } from 'uuid';
import { getCOSPresigned } from '../../service/service';
import { uniqBy, isEmpty, findLastIndex } from 'lodash-es';
import { CHAT_ENGINE_MAP, STORAGE_KEY } from '../../config/constant';
import type { ChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { resolveHistoryDisplayQuery, resolveKbExpandToken } from '../../components/QAInput/utils/inlineMentionLabel';

import {
  appendAgentHistoryTextPart,
  getHistoryFoldMode,
  getNextSwitchAgentInputIndex,
  resolveAgentHistoryThinkId,
} from './thinkFoldAgent';

// (Code Review Fix #9) 正则表达式常量缓存，避免每次调用 preprocessMarkdown 都创建新对象
const REGEX_SINGLE_TILDE = /([^~])~([^~])/g;
const REGEX_BOLD_MARKDOWN = /\*\*([^*\n]+?)\*\*/g;
const REGEX_REFERENCE_TAG = /(\r?\n\s*)<reference>/g;
const REGEX_DATABRAIN_DATA = /<databraindata>/g;

/**
 * 安全解析 JSON 字符串
 * @param jsonString - 要解析的 JSON 字符串
 * @returns 解析后的对象，如果解析失败则返回原始值
 */
export const parseJsonSafely = (jsonString: any): any => {
  if (typeof jsonString !== 'string') {
    return jsonString;
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.warn('Failed to parse JSON:', error);
    return jsonString;
  }
};

/**
 * 将任意 database_query value 归一为 IDatabaseQueryGroup[]
 */
export const normalizeDatabaseQueryValue = (raw: unknown): IDatabaseQueryGroup[] => {
  const parsed = parseJsonSafely(raw);
  if (Array.isArray(parsed)) return parsed as IDatabaseQueryGroup[];
  if (parsed && typeof parsed === 'object') return [parsed as IDatabaseQueryGroup];
  return [];
};

/** 历史 part 的 thinkId：tool 模式用 metadata.id，与流式 result.id 对齐 */
const resolveThinkId = (
  metadata?: { id?: string; think_id?: string; },
  data?: { think_id?: string; id?: string; },
  item?: { id?: string; },
): string =>
  metadata?.id ||
  metadata?.think_id ||
  data?.think_id ||
  data?.id ||
  item?.id ||
  '';
const getThinkContainer = (
  formattedAnswer: Array<
  | IAnswerItemThink
  | IAnswerItemText
  | IAnswerItemReference
  | IAnswerItemBiData
  | IAnswerItemError
  | IAnswerSensitive
  | IAnswerCostWarning
  | IAnswerItemEchartsOption
  | IAnswerItemDataDescription
  | IAnswerItemBqChart
  >,
): IAnswerItemThink => {
  for (let i = formattedAnswer.length - 1;i >= 0;i--) {
    const item = formattedAnswer[i];
    if (item?.type === 'think') {
      return item as IAnswerItemThink;
    }
  }
  const thinkContainer: IAnswerItemThink = {
    type: 'think',
    status: 'success',
    value: [],
  };
  formattedAnswer.push(thinkContainer);
  return thinkContainer;
};

/** tool 模式：判断当前 text part 之后是否还有工具相关数据 */
const TOOL_ACTIVITY_DATA_TYPES = new Set([
  'tool_use',
  'tool_output',
  'think_tool',
  'text_tool',
]);

const hasToolActivityAfter = (
  parts: Array<{
    type?: string;
    data?: { type?: string; };
    metadata?: { tool_call_id?: string; };
  }>,
  fromIndex: number,
): boolean => {
  for (let i = fromIndex + 1;i < parts.length;i += 1) {
    const part = parts[i];
    if (part?.type === 'data' && part?.data?.type) {
      if (TOOL_ACTIVITY_DATA_TYPES.has(part.data.type)) {
        return true;
      }
    }
    if (part?.type === 'text' && part?.metadata?.tool_call_id) {
      return true;
    }
  }
  return false;
};

/** think 内联 text 按后端顺序追加到 think.value */
const pushThinkInlineText = (
  thinkContainer: IAnswerItemThink,
  thinkItem: {
    type: 'text';
    value: string;
    thinkId?: string;
    tool_call_id?: string;
  },
): void => {
  if (!Array.isArray(thinkContainer.value)) {
    thinkContainer.value = [];
  }
  const { tool_call_id: toolCallId, value } = thinkItem;
  if (toolCallId) {
    const existing = thinkContainer.value.find(
      (v: { type?: string; tool_call_id?: string; }) =>
        v?.type === 'text' && v?.tool_call_id === toolCallId,
    );
    if (existing) {
      existing.value += value;
      return;
    }
  }
  thinkContainer.value.push(thinkItem as never);
};

/**
 * 将 res.messages 格式化成 Conversation 的数据结构
 * @param messages - 原始消息数组
 * @returns 格式化后的 Conversation 数组
 */
export const formatMessagesToConversations = (messages: any[]): Conversation[] => {
  if (!Array.isArray(messages)) {
    return [];
  }
  const params = new URLSearchParams(window.location.search);
  const template = params.get('template');
  const share = params.get('snapshotId');
  const poolId = params.get('poolId');
  // TODO: 调整动机：ds报告下，后端返回的协议和落地页的有出入，需要兼容一下这2个页面，防止展示异常
  const showStep = template === 'dsReport' || share || poolId;

  return messages.map((message) => {
    const {
      message_id,
      answer: rawAnswer = [],
      query,
      // timestamp,
      like = 0,
      think_time_seconds = 0,
      mode = '',
      error_code,
      model_id ='',
      status,
      system_language,
      metadata,
      frontend_metadata
    } = message;
    // const query = "{\"role\": \"user\", \"parts\": [{\"type\": \"text\", \"text\": \"What is the contents of the image?\", \"metadata\": null}, {\"type\": \"file\", \"file\": {\"name\": null, \"mimeType\": \"image\", \"bytes\": null, \"uri\": \"https://ogdb-cdn.intlgame.cn/company/clipboard_20250620_111319.png\"}, \"metadata\": {\"detail\": \"auto\", \"image_type\": \"\", \"size\": 40.0}}], \"metadata\": null}"
    // 解析 query 字符串
    let queryText = '';
    let queryFileList = [];
    let queryRawParts: any[] = [];
    let customQuestion = false;
    let thinkTimeSeconds = think_time_seconds;
    try {
      const queryObj = typeof query === 'string' ? JSON.parse(query) : query;
      // chart 类 part 原样保留（保留 file.mimeType='chart' 与 metadata.chart_ids），渲染层按 mimeType 过滤即可，
      // edit/refresh 时随 fileList 回流，buildFileListData 内识别后并入 chart_ids
      queryFileList = (queryObj?.parts || [])
        .filter((item: { type: string; }) => item.type === 'file')
        .map((fileItem: { metadata: { name: string; image_type: any; size: any; }; file: { uri: string; id: any; mimeType?: string; }; }) => {
          if (fileItem?.file?.mimeType === 'chart') {
            // 补 status='success'，让 QAInput 的 successList 过滤能接住；其余字段保持原 part 结构供后端透传
            return { ...fileItem, status: 'success' };
          }
          const previewName = fileItem?.metadata?.name || '';
          // 外部来源（opinion_pc / aigallery_report）的文件不展示在历史 QA 文件列表中
          const isExternal = ['opinion_pc', 'aigallery_report'].includes(frontend_metadata?.form);
          return {
            ...fileItem,
            fileSrc: fileItem?.file?.uri || '',
            originalSrc: fileItem?.file?.uri || '',
            type: fileItem?.metadata?.image_type || fileItem?.file?.mimeType || fileItem?.file?.uri?.split('.').pop()?.toLowerCase() || '',
            size: fileItem?.metadata?.size,
            status: 'success',
            name: getPreviewName(previewName),
            previewName: previewName.substring(0, previewName?.lastIndexOf('.')),
            id: fileItem?.file?.id || uuidv4(),
            ...(isExternal ? { external: true } : {}),
          };
        });
      if (queryObj?.parts?.[0]?.text) {
        queryText = queryObj.parts[0].text;
      }
      queryRawParts = (queryObj?.parts || []).filter((p: any) => p.type !== 'text');
    } catch (error) {
      console.warn('Failed to parse query:', error);
      queryText = typeof query === 'string' ? query : '';
      queryFileList = [];
    }

    const answerParts = Array.isArray(rawAnswer)
      ? rawAnswer
      : (typeof rawAnswer === 'string' ? parseJsonSafely(rawAnswer) : []);
    const normalizedAnswer = Array.isArray(answerParts) ? answerParts : [];
    const historyFoldMode = getHistoryFoldMode(normalizedAnswer);
    const isToolFoldMode = historyFoldMode === 'tool';
    const lastTextPartIndex = findLastIndex(
      normalizedAnswer,
      (part: { type?: string; }) => part?.type === 'text',
    );

    // 格式化 answer 数组
    const formattedAnswer: Array<IAnswerItemThink | IAnswerItemText | IAnswerItemReference | IAnswerItemBiData | IAnswerItemError | IAnswerSensitive | IAnswerCostWarning | IAnswerItemEchartsOption | IAnswerItemDataDescription | IAnswerItemBqChart | IAnswerItemOpinionReportCard | IAnswerItemDatabaseQuery> = [];
    // let prevAgent = '';
    normalizedAnswer.forEach((item: any, partIndex: number) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { type, data, text, metadata } = item;

      if (type === 'text' && text) {
        if (!isToolFoldMode) {
          const thinkContainer = getThinkContainer(formattedAnswer);
          const target = appendAgentHistoryTextPart({
            thinkContainer,
            text,
            metadata,
          });
          if (target === 'think') {
            return;
          }
          const textItem: IAnswerItemText = {
            type: 'text',
            value: text,
          };
          formattedAnswer.push(textItem);
          return;
        }

        const thinkId = resolveThinkId(metadata, undefined, item);
        // 最后一段 text 为正文；其后无 tool 活动时也不进 think（正文可能仍带末次 tool_call_id）
        const shouldInlineTextToThink =
          partIndex !== lastTextPartIndex &&
          (metadata?.tool_call_id || thinkId) &&
          hasToolActivityAfter(normalizedAnswer, partIndex);

        if (shouldInlineTextToThink) {
          pushThinkInlineText(getThinkContainer(formattedAnswer), {
            type: 'text',
            value: text,
            thinkId,
            ...(metadata?.tool_call_id
              ? { tool_call_id: metadata.tool_call_id }
              : {}),
          });
          return;
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        formattedAnswer.push({
          type: 'text',
          value: text,
        } as IAnswerItemText);
        return;
      }

      if (type === 'data' && data) {
        const {
          type: dataType,
          value,
          title,
          new_agent,
          previous_agent,
          input,
          data_type: bqDataType,
        } = data;

        // 第一步：过滤 Planner Agent、Summary Agent 切换
        if (dataType === 'switch_agent' && (new_agent === 'Planner Agent' || new_agent === 'Summary Agent')) {
          return;
        }
        // 处理 think 类型
        if (dataType === 'think') {
          getThinkContainer(formattedAnswer).value.push({
            type: 'think',
            value,
          });
          return;
        }

        // 处理 plan_json 类型
        if (dataType === 'plan_json') {
          let planValue = value;
          try {
            const parsed = JSON.parse(value);
            if (parsed?.rephrase) {
              planValue = parsed.rephrase;
            }
          } catch (e) {
            // 解析失败时保持原值
          }

          getThinkContainer(formattedAnswer).value.push({
            type: 'plan_json',
            value: planValue,
            buried: value,
          });
          return;
        }

        if (dataType === 'think_fold_mode') {
          const thinkContainer = getThinkContainer(formattedAnswer);
          const foldMode = data?.value ?? value;
          const existing = thinkContainer.value.find(
            (v: { type?: string; }) => v?.type === 'think_fold_mode',
          );
          if (existing) {
            existing.value = foldMode;
          } else {
            thinkContainer.value.unshift({
              type: 'think_fold_mode',
              value: foldMode,
            });
          }
          return;
        }

        // 处理 switch_agent 的特殊情况
        if (dataType === 'switch_agent') {
          const thinkContainer = getThinkContainer(formattedAnswer);
          const switchThinkId = isToolFoldMode
            ? resolveThinkId(metadata, data, item)
            : resolveAgentHistoryThinkId(metadata, data);
          const switchAgentItem = {
            type: 'switch_agent' as const,
            new_agent,
            previous_agent,
            input,
            value: input || '',
            thinkId: switchThinkId,
            ...(!isToolFoldMode
              ? { inputIndex: getNextSwitchAgentInputIndex(thinkContainer) }
              : {}),
          };
          thinkContainer.value.push(switchAgentItem as never);
          return;
        }

        if (dataType === 'tool_use') {
          getThinkContainer(formattedAnswer).value.push({
            type: 'tool_use',
            value: value ?? data?.value ?? '',
            tool_call_id: data?.tool_call_id || '',
            thinkId: isToolFoldMode
              ? resolveThinkId(metadata, data, item)
              : resolveAgentHistoryThinkId(metadata, data),
          });
          return;
        }

        if (dataType === 'think_tool') {
          getThinkContainer(formattedAnswer).value.push({
            type: 'think_tool',
            value: value || '',
            tool_call_id: data?.tool_call_id || '',
            thinkId: isToolFoldMode
              ? resolveThinkId(metadata, data, item)
              : resolveAgentHistoryThinkId(metadata, data),
          });
          return;
        }

        if (dataType === 'text_tool') {
          const thinkContainer = getThinkContainer(formattedAnswer);
          const thinkItem = {
            type: (isToolFoldMode ? 'text' : 'text_tool') as 'text' | 'text_tool',
            value: value || '',
            tool_call_id: data?.tool_call_id || '',
            thinkId: isToolFoldMode
              ? resolveThinkId(metadata, data, item)
              : resolveAgentHistoryThinkId(metadata, data),
          };
          const existing = thinkContainer.value?.find(
            (v: { type?: string; tool_call_id?: string; }) =>
              (v?.type === 'text' || v?.type === 'text_tool') &&
              v?.tool_call_id === data?.tool_call_id,
          );
          if (existing) {
            existing.value += value || '';
          } else {
            thinkContainer.value.push(thinkItem as never);
          }
          return;
        }

        if (dataType === 'tool_output') {
          const toolCallId = data?.tool_call_id || '';
          const latency = data?.latency;
          const thinkId = isToolFoldMode
            ? resolveThinkId(metadata, data, item)
            : resolveAgentHistoryThinkId(metadata, data);
          const thinkContainer = getThinkContainer(formattedAnswer);
          const toolUseItem = thinkContainer.value?.find(
            (v: { type?: string; tool_call_id?: string; thinkId?: string; }) =>
              v?.type === 'tool_use' &&
              v?.tool_call_id === toolCallId &&
              (!thinkId || v?.thinkId === thinkId),
          );
          if (toolUseItem) {
            (toolUseItem as { latency?: number; }).latency = latency;
          }
          return;
        }




        if (dataType === 'reference') {
          const referenceValue = parseJsonSafely(value);
          const referenceArray = Array.isArray(referenceValue) ? referenceValue : [referenceValue];

          // 查找最后一个项是否也是 reference 类型
          const lastIndex = formattedAnswer.length - 1;
          const lastItem = formattedAnswer[lastIndex];

          if (lastItem && lastItem.type === 'reference') {
            // 如果最后一项是 reference 类型，则合并到其 value 数组中
            (lastItem as IAnswerItemReference).value.push(...referenceArray);
          } else {
            // 创建新的 reference 项
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            formattedAnswer.push({
              type: 'reference',
              value: referenceArray
            } as IAnswerItemReference);
          }
          return;
        }

        if (dataType === 'bi_data') {
          const biDataValue = parseJsonSafely(value);

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          formattedAnswer.push({
            type: 'biData',
            value: biDataValue
          } as IAnswerItemBiData);
          return;
        }

        if (dataType === 'echarts_option') {
          const echartsOption = parseJsonSafely(value);

          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          formattedAnswer.push({
            type: 'echartsOption',
            value: echartsOption
          } as IAnswerItemEchartsOption);
          return;
        }

        if (dataType === 'bq_chart') {
          const bqChartOption = parseJsonSafely(value);
          // data_type === 'think' 表示多agent场景，图表嵌入think块中展示
          if (bqDataType === 'think') {
            const thinkItem = { type: 'bq_chart' as const, value: bqChartOption };
            // 向前搜索最后一个 think 容器（text 可能插在 think 和 bq_chart 之间，不能只看最后一项）
            let existingThinkItem: any = null;
            for (let i = formattedAnswer.length - 1;i >= 0;i--) {
              if (formattedAnswer[i].type === 'think') {
                existingThinkItem = formattedAnswer[i];
                break;
              }
            }
            if (existingThinkItem && Array.isArray(existingThinkItem.value)) {
              existingThinkItem.value.push(thinkItem);
            } else {
              // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
              formattedAnswer.push({
                type: 'think',
                status: 'success',
                value: [thinkItem]
              } as unknown as IAnswerItemThink);
            }
          } else {
            // data_type === 'text' 或未指定，渲染在正文（原有行为）
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            formattedAnswer.push({
              type: 'bq_chart',
              value: bqChartOption
            } as IAnswerItemBqChart);
          }
          return;
        }

        if (dataType === 'sensitive_data_info') {
          // 历史数据仅保留第一条 sensitiveInfo，后续同类数据忽略
          const existingSensitive = formattedAnswer.find(answerItem => answerItem.type === 'sensitiveInfo');
          if (existingSensitive) {
            return;
          }
          const sensitiveValue = parseJsonSafely(value);
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const sensitiveItem = {
            type: 'sensitiveInfo',
            value: sensitiveValue
          } as IAnswerSensitive;
          // 插入到 think后面，兼容历史数据顺序
          const insertIdx = formattedAnswer.findIndex(item => item.type === 'think') + 1;
          formattedAnswer.splice(insertIdx, 0, sensitiveItem);
          return;
        }

        if (dataType === 'warning') {
          const existingWarning = formattedAnswer.find(
            answerItem => answerItem.type === 'costWarning',
          );
          if (existingWarning) {
            return;
          }
          const warningValue = parseJsonSafely(value);
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const warningItem = {
            type: 'costWarning',
            value: warningValue,
          } as IAnswerCostWarning;
          const insertIdx = formattedAnswer.findIndex(item => item.type === 'think') + 1;
          formattedAnswer.splice(insertIdx, 0, warningItem);
          return;
        }
        
        if (
          dataType === 'document' ||
          dataType === 'ai_excel_file' ||
          dataType === 'magic_poster_file'
        ) {
          const documentValue = parseJsonSafely(value);
          // 统一使用 type: 'document' 渲染 DocumentCmp，通过 subType 传递真实类型用于组件映射
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          formattedAnswer.push({
            type: 'document',
            value: documentValue,
            subType: dataType
          } as unknown as IAnswerSensitive);
          if (documentValue?.src === 'click') {
            customQuestion = true;
          }
          return;
        }   
        if (dataType === 'opinion_report_card') {
          const opinionReportCardValue = parseJsonSafely(value);
          const opinionReportCardArray = Array.isArray(opinionReportCardValue)
            ? opinionReportCardValue
            : [opinionReportCardValue];

          const lastIndex = formattedAnswer.length - 1;
          const lastItem = formattedAnswer[lastIndex];

          if (lastItem && lastItem.type === 'opinion_report_card') {
            formattedAnswer[lastIndex] = {
              ...(lastItem as IAnswerItemOpinionReportCard),
              value: [
                ...(lastItem as IAnswerItemOpinionReportCard).value,
                ...opinionReportCardArray,
              ],
            };
          } else {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            formattedAnswer.push({
              type: 'opinion_report_card',
              value: opinionReportCardArray,
            } as IAnswerItemOpinionReportCard);
          }
          return;
        }

        if (dataType === 'think_mode') {
          const thinkModeItem = {
            subType: 'think_mode' as const,
            type: 'think' as const,
            value,
          };
          getThinkContainer(formattedAnswer).value.unshift(thinkModeItem);
          return;
        }

        if ((showStep) && dataType === 'think_time_seconds') {
          thinkTimeSeconds = value;
        }

        if (dataType === 'data_description' || dataType === 'complete_summary') {
          const dataDescriptionItem: IAnswerItemDataDescription = {
            type: dataType,
            title: title || '',
            value: value || ''
          };
          formattedAnswer.push(dataDescriptionItem);
          return;
        }

        if (dataType === 'current_token_total') {
          getThinkContainer(formattedAnswer).value.push({
            type: 'current_token_total',
            value: Number(value) || 0,
          });
          return;
        }

        // 知识库引用：与流式一致，使用 splice 插入到 think 之后
        if (dataType === 'database_query') {
          const citationArray = normalizeDatabaseQueryValue(value);
          if (citationArray.length === 0) return;

          const existingItem = formattedAnswer.find(
            (item) => item.type === 'database_query',
          ) as IAnswerItemDatabaseQuery | undefined;
          if (existingItem) {
            existingItem.value.push(...citationArray);
          } else {
            const thinkIdx = formattedAnswer.findIndex(item => item.type === 'think');
            formattedAnswer.splice(thinkIdx + 1, 0, {
              type: 'database_query',
              value: [...citationArray],
            });
          }
          return;
        }
      }
    });

    // 如果状态为1(流式中断)
    // 只保留第一个error，与流式数据处理保持一致
    let hasExistingError = formattedAnswer.some(item => item.type === 'error');
    if (message.status === 1 && !hasExistingError) {
      // 只有不存在error数据时，才添加新的error
      formattedAnswer.push({
        type: 'error',
        value: 'stop'
      });
      hasExistingError = true; // 标记已添加error，防止后续重复添加
    }
    if (error_code && !hasExistingError) {
      // 只有不存在error数据时，才添加新的error
      formattedAnswer.push({
        type: 'error',
        value: {code: error_code}
      } as any);
    }
    // 如果template 等于dsReport ，这个时候，需要把超第二项think（或者超出第二项的所有think数据）的value全部 push到第一个think里面
    if (showStep) {
      const thinkItems = formattedAnswer.filter(item => item.type === 'think');
      if (thinkItems.length > 1) {
        const firstThink = thinkItems[0];
        for (let i = 1;i < thinkItems.length;i++) {
          const currentThink = thinkItems[i];
          if (Array.isArray(firstThink.value) && Array.isArray(currentThink.value)) {
            firstThink.value.push(...currentThink.value);
            formattedAnswer.splice(formattedAnswer.indexOf(currentThink), 1);
          }
        }
      }
      const thinkIndex = formattedAnswer.findIndex(item => item.type === 'think');
      // 如果thinkIndex大于0，需要把thinkItem放到最前面，防止后端数据异常
      if (thinkIndex > 0) {
        const thinkItem = formattedAnswer[thinkIndex];
        formattedAnswer.splice(thinkIndex, 1);
        formattedAnswer.unshift(thinkItem);
      }
    }
    // uniqBy 会根据迭代函数的返回值进行去重，保留第一个匹配的元素
    // 对于 type === 'think' 的项，返回固定值 'think'，这样所有 think 项会被识别为相同，只保留第一个
    // 对于其他类型的项，返回 Math.random()，确保每个都是唯一的，全部保留
    const finalAnswer = uniqBy(formattedAnswer, item => item.type === 'think' ? 'think' : Math.random());

    // 气泡 / 编辑态展示用折叠态 display_query（≥3 篇为 `@库名 N篇`）；
    // parts.text 可能是提交给模型的平铺串，不能直接当历史提问文案
    const rawFm = typeof frontend_metadata === 'string'
      ? parseJsonSafely(frontend_metadata)
      : frontend_metadata;
    const metaFm = typeof metadata === 'object' && metadata
      ? (
        typeof (metadata as { frontend_metadata?: unknown; }).frontend_metadata === 'string'
          ? parseJsonSafely((metadata as { frontend_metadata: string; }).frontend_metadata)
          : (metadata as { frontend_metadata?: Record<string, unknown>; }).frontend_metadata
      )
      : null;
    let resolvedFm = (
      rawFm && typeof rawFm === 'object'
        ? { ...(metaFm || {}), ...rawFm }
        : metaFm
    ) as Record<string, unknown> | null | undefined;

    const displayQuery = (resolvedFm as { display_query?: string; } | null | undefined)
      ?.display_query;
    let kbExpandToken = (resolvedFm as {
      kb_expand_token?: { from: string; to: string; };
    } | null | undefined)?.kb_expand_token;

    // 历史消息回写 kb_expand_token，供编辑/刷新时 replace 展开
    if (
      resolvedFm
      && typeof resolvedFm === 'object'
      && displayQuery?.trim()
      && queryText?.trim()
      && displayQuery.trim() !== queryText.trim()
      && !kbExpandToken
    ) {
      const token = resolveKbExpandToken(
        displayQuery.trim(),
        queryText.trim(),
        kbExpandToken,
      );
      if (token) {
        kbExpandToken = token;
        resolvedFm = {
          ...resolvedFm,
          kb_expand_token: token,
        };
      }
    }

    const resolvedQuery = resolveHistoryDisplayQuery({
      modelQuery: queryText,
      displayQuery,
      kbExpandToken,
    });

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      messageId: message_id,
      query: resolvedQuery,
      fileData: queryFileList,
      answer: finalAnswer,
      like,
      thinkTimeSeconds,
      mode,
      modeId: metadata?.model_id || model_id,
      customQuestion,
      status,
      system_language,
      metadata,
      frontend_metadata: {
        ...(resolvedFm || frontend_metadata || {}),
        rawParts: queryRawParts,
      },
    } as Conversation;
  });
};

// 兼容原有的函数名 - 但这个版本返回新的数据结构
export const transformMessages = formatMessagesToConversations;

/**
 * 使用示例：
 * 
 * import { formatMessagesToConversations } from '../utils';
 * 
 * // 在 useRequest 中使用
 * const { data, loading, mutate, run: fetchHistoryList } = useRequest(() => {
 *   const { openid } = userInfo || {};
 *   return getHistoryList({ sessionId: routeSessionId, openid }).then((res) => {
 *     // 使用格式化函数处理历史消息
 *     return formatMessagesToConversations(res?.messages || []);
 *   }, () => {
 *     return [];
 *   });
 * }, {
 *   manual: true,
 * });
 * 
 * // 或者直接调用
 * const formattedConversations = formatMessagesToConversations(res.messages);
 */

/**
   * 获取指定类名集合中的最后一个元素
   *
   * @param {string} className - 要查询的CSS类名
   * @returns {Element|null} 返回最后一个元素，如果未找到返回null
   */
export const getLastElementByClassName = (className: string) => {
  const elements = document.querySelectorAll(`.${className}`);

  if (elements.length > 0) {
    return elements[elements.length - 1];
  }

  return null;
};

/**
 * 获取元素距离文档顶部的距离（包括滚动距离）
 *
 * @param {Element} element - DOM元素
 * @returns {number} 元素顶部到文档顶部的距离（像素）
 */
export const getElementTopPosition = (element: Element): number => {
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  return rect.top + scrollTop;
  // return rect.top;
};

export const preprocessMarkdown = (input: string) => {
  // TODO: 整个markdown解析有问题的应该暴露到后端侧，源头闭环，前端处理，容易有性能问题
  try {
    if (!input) return input;

    /**
     * 在这里我们只去处理单个 `~` 和成对的 `~~`
     * - 对于两个 `~~`：保留原样，让 ReactMarkdown 按正常的删除线语法解析。
     * - 对于单个 `~`：需要手动转义，确保其不会被意外解析。
     */
    let processed = input.replace(REGEX_SINGLE_TILDE, '$1&#126;$2');

    /**
     * 最简单实现"加粗不分裂、也不展示 ** 语法"
     * 直接把 markdown 的 **xxx** 转成 HTML 的 <strong>xxx</strong>
     * （项目已启用 rehypeRaw，strong 标签会被正常渲染）
     */
    processed = processed.replace(REGEX_BOLD_MARKDOWN, '<strong>$1</strong>');

    /**
     *  处理 <reference> 这种形式的标签, 待后端调整方案，前端先放开
     */
    processed = processed.replace(REGEX_REFERENCE_TAG, '<reference>');

    /**
     * <databraindata> 在前面加一个换行符 特殊处理<reference>标签后面紧贴<databraindata>
     */
    processed = processed.replace(REGEX_DATABRAIN_DATA, '\n<databraindata>');

    return processed;
  } catch (error) {
    console.log('error', error);
    return input;
  }
};

export interface IConvertTimeFormatOptions {
  /** 输入时间单位，默认 `s`（秒） */
  unit?: 's' | 'ms';
  /** 是否支持毫秒级展示（不足 1 秒显示 ms，1~60 秒显示带一位小数 s） */
  supportMs?: boolean;
}

/**
 * 将时间转换为可读格式
 * @param timeStr - 时间数值字符串
 * @param options - 可选：输入单位、是否支持毫秒
 * @returns 如 "13min20s"、"45s"、"650ms"、"1.5s"
 */
export function convertSecondsToTimeFormat(
  timeStr: string,
  options?: IConvertTimeFormatOptions,
): string {
  if (!timeStr || isNaN(Number(timeStr))) {
    return '';
  }

  const { unit = 's', supportMs = false } = options || {};
  const totalMs = unit === 'ms' ? Number(timeStr) : Number(timeStr) * 1000;

  if (totalMs <= 0) {
    return '';
  }

  if (supportMs) {
    if (totalMs < 1000) {
      const roundedMs = Math.round(totalMs * 100) / 100;
      return `${roundedMs}ms`;
    }
    if (totalMs < 60 * 1000) {
      return `${(totalMs / 1000).toFixed(1)}s`;
    }
  }

  const totalSeconds = Math.floor(totalMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}min${seconds}s`;
  }
  return `${seconds}s`;
}

/**
 * 递归提取 Markdown 自定义标签 children 中的文本内容
 * 解决 remark-gfm autolink 将 URL 转为 <a> 标签导致纯文本提取不完整的问题
 */
export const extractMarkdownChildrenText = (node: React.ReactNode): string => {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (!node) return '';
  if (Array.isArray(node)) return node.map(extractMarkdownChildrenText).join('');
  if (React.isValidElement(node)) {
    const props = node.props as { href?: string; children?: React.ReactNode; };
    if (props?.href && (node.type === 'a' || node.type === 'A')) {
      return props.href;
    }
    return extractMarkdownChildrenText(props?.children);
  }
  return '';
};

export const extractAndValidateJson = (children: React.ReactNode) => {
  const content = extractMarkdownChildrenText(React.Children.toArray(children)).trim();

  // 验证JSON完整性
  const isValidJson = (str: string) => {
    try {
      JSON.parse(str);
      return str.startsWith('{') && str.endsWith('}');
    } catch {
      return false;
    }
  };

  if (!isValidJson(content)) {
    return null;
  }

  return parseJsonSafely(content);
};

interface IQuickInputButtonPayload {
  button_list?: Array<{ query?: string; type?: string; }>;
}

export interface IQuickInputButtonItem {
  query: string;
  type?: string;
}

/** 解析 <btn> 标签内容，返回 button_list 全部按钮 */
export const getBtnButtonList = (
  children: React.ReactNode,
): IQuickInputButtonItem[] => {
  const payload = extractAndValidateJson(children) as IQuickInputButtonPayload | null;
  const fromJson = payload?.button_list
    ?.map(item => ({
      query: String(item?.query || '').trim(),
      type: item?.type,
    }))
    .filter(item => item.query);
  if (fromJson?.length) return fromJson;

  const text = extractMarkdownChildrenText(children);
  const matches = [...text.matchAll(/query:\s*([^,}\]]+)/g)];
  return matches
    .map(match => ({ query: match[1]?.trim() || '' }))
    .filter(item => item.query);
};


/**
 * 解析代理响应数据，提取第一个 think 中的 reasoning 内容和 plan_json 值
 * @param {Array} agentResponse - 代理响应数据数组
 * @returns {Object} 包含 reasoning 内容和 plan_json 值的对象
 */
export function parseAgentThink(agentResponse: any[]) {
  // 查找第一个 type 为 "think" 的项
  const firstThink = agentResponse?.find(item => item.type === "think");

  if (!firstThink || !Array.isArray(firstThink.value)) {
    // throw new Error("未找到有效的 think 数据");
    return {
      rephrase: "",
      plan: null
    };
  }
  const thinkData = firstThink.value;
  // 查找包含 reasoning 的 think 子项
  const reasoningItem = thinkData.find((item: { type: string; }) =>
    item.type === "plan_json"
  );
  // TODO: 优化查找 plan_json 子项
  const planJsonItem = parseJsonSafely(reasoningItem?.buried || reasoningItem?.value);
  return {
    rephrase: planJsonItem?.rephrase,
    plan: planJsonItem?.plans
  };
}

// 对从接口获取的图片进行统一签名处理，页面中处理会重复签名，这里统一处理
export const processData = async (data: any[]) => {
  const hasFileData = data.some(item => item.fileData?.length > 0);
  if (!hasFileData) return data;
  try {
    const urlMap = new Map(); // 存储 原始fileSrc -> 签名URL 的映射
    const imgList: string[] = []; // 收集待签名的原始URL

    // 第一次遍历：收集所有 fileSrc（保持原始编码）
    data.forEach(item => {
      if (item.fileData?.length > 0) {
        item.fileData.forEach((file: { fileSrc: string; }) => {
          if (file.fileSrc && !urlMap.has(file.fileSrc)) {
            imgList.push(file.fileSrc); // 不修改原始URL
            urlMap.set(file.fileSrc, null); // 初始化映射
          }
        });
      }
    });

    // 调用签名接口
    if (imgList.length > 0) {
      const res = await getCOSPresigned({
        "cos_type": "agentCos",
        "url": imgList // 直接传入原始URL
      });
      const signedUrls = res?.url || [];
      // 更新映射：原始URL -> 签名URL
      imgList.forEach((url, index) => {
        urlMap.set(url, signedUrls[index]);
      });
    }

    // 第二次遍历：仅替换 fileSrc，不修改 file 对象
    const newData = JSON.parse(JSON.stringify(data));
    newData.forEach((item: { fileData: { fileSrc: any; }[]; }) => {
      if (item.fileData?.length > 0) {
        item.fileData.forEach((fileItem: { fileSrc: any; }) => {
          if (fileItem.fileSrc && urlMap.has(fileItem.fileSrc)) {
            const signedUrl = urlMap.get(fileItem.fileSrc);
            if (signedUrl) {
              fileItem.fileSrc = signedUrl; // 仅更新 fileSrc
              // 不再修改 file.uri，保持原样
            }
          }
        });
      }
    });
    return newData;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    return data;
  }
};

/**
 * 从错误字符串中提取错误消息（只关心 `message` 字段）
 * @param {string} errorString - 包含错误信息的字符串
 * @returns {string} - 提取的错误消息，如果解析失败则返回 "Unknown error"
 */
export function extractErrorMessage(errorString: string) {
  if(!errorString) {
    return '';
  }
  try {
    // 1. 尝试直接匹配 `message` 字段的值（更高效，避免完整解析 JSON）
    // 使用正则表达式匹配 `'message': '...'` 或 `"message": "..."`
    const messageMatch = errorString.match(/(?:'message'|"message")\s*:\s*['"](.*?)['"]/);
    if (messageMatch?.[1]) {
      return messageMatch?.[1]; // 直接返回匹配到的消息内容
    }

    // 2. 如果正则匹配失败，尝试完整解析 JSON（兼容性更强）
    const jsonMatch = errorString.match(/\{.*\}/);
    if (!jsonMatch) throw new Error("");

    // 修复 JSON 字符串（单引号 → 双引号，None → null）
    const fixedJson = jsonMatch[0]
      .replace(/'/g, '"')
      .replace(/None/g, 'null');

    // 解析 JSON 并提取 message
    const errorData = JSON.parse(fixedJson);
    return errorData.error?.message;

  } catch (error) {
    console.error("Error parsing message:", error);
    return "";
  }
}

export function hasReportDocument(data) {
  if (isEmpty(data) || !Array.isArray(data)) {
    return false;
  }
  return data.some(
    (item) =>
      item.answer &&
      Array.isArray(item.answer) &&
      item.answer.some(
        (answerItem) =>
          answerItem.type === 'document' &&
          answerItem.value &&
          answerItem.value.type === 'report',
      ),
  );
}

export function getLastDocumentId(data: any[]) {
  if (!Array.isArray(data) || data?.length === 0) {
    return null; // 处理空数组或非数组输入
  }
  const lastItem = data[data.length - 1];
  if (!lastItem.answer || !Array.isArray(lastItem.answer)) {
    return null; // 处理answer字段不存在或非数组的情况
  }
  const documentItem = lastItem.answer.find(
    (item: { type: string; }) => item.type === 'document',
  );
  return documentItem ? documentItem?.value : null;
}

export function removeTags(text: string) {
  if (!text) {
    return '';
  };
  
  let value = text;
  // 移除 <reference> 标签及其内容
  value = value.replace(/<reference>[\s\S]*?<\/reference>/g, '');
  // 移除bid标签
  value = value.replace(/<bid>[\s\S]*?<\/bid>/g, '');
  // 以及![image]开头的
  value = value.replace(/!\[image\]\(.*?\)/g, '');
  // 移除空行（可选）
  // value = value.replace(/^\s*[\r\n]/gm, '');

  // return value.trim();
  return value;
}

export function countDocumentTypes(data) {
  if (!Array.isArray(data) || data?.length === 0) {
    return 0; // 处理空数组或非数组输入
  }
  let count = 0;
  for (const item of data) {
    for (const answerItem of item.answer) {
      if (answerItem.type === 'document') {
        // eslint-disable-next-line no-plusplus
        count++;
      }
    }
  }
  return count;
}

export const getPreviewName = (str: string) => {
  return str?.substring(0, str?.lastIndexOf('.')) || '';
};


/**
   * 处理批量chunk数据
   * @param data - 可能是字符串数组或普通对象
   * @param messageId - 消息ID
   */
export const processBatchChunks = (data: any, messageId: string, processStream: (chunk: any, messageId: string) => void, callback) => {
  try {
    // 判断是否是数组格式的字符串（以 [ 开头，] 结尾）
    if (typeof data === 'string' && data.trim().startsWith('[') && data.trim().endsWith(']')) {
      // console.log('检测到批量chunk数据，开始处理');
      let chunksArray: string[];
      try {
        // 去除最外层的方括号（例如：'[{...},{...}]' -> '{...},{...}'）
        const cleanString = data.slice(1, -1);
        // 按照 '},{' 分割字符串，将多个JSON对象分离
        // 例如：'{a:1},{b:2},{c:3}' -> ['{a:1', 'b:2', 'c:3}']
        // TODO: 这个待优化
        chunksArray = cleanString.split('},{')
          .map((item, index, array) => {
            // 第一个元素：已有左花括号，补充右花括号
            // 例如：'{a:1' -> '{a:1}'
            if (index === 0) return `${item  }}`;
            
            // 最后一个元素：已有右花括号，补充左花括号
            // 例如：'c:3}' -> '{c:3}'
            if (index === array.length - 1) return `{${  item}`;
            
            // 中间元素：两端都缺少花括号，需要补充完整
            // 例如：'b:2' -> '{b:2}'
            return `{${  item  }}`;
          });
      } catch (e) {
        chunksArray = [];
        // 如果整体解析失败，尝试更宽松的解析方式
        console.log(e);
      }

      // 依次处理每一个chunk
      chunksArray.forEach((chunkStr: string, index: number) => {
        try {
          if (index === 0) {
            callback?.();
          }
          const chunk = parseJsonSafely(chunkStr);
          const formattedChunk = {
            data: JSON.stringify(chunk)
          };
          processStream(formattedChunk, messageId);
        } catch (error) {
          console.error(`解析第 ${index + 1} 条chunk失败:`, error, chunkStr);
        }
      });
      // console.log(`批量处理完成，共处理 ${chunksArray.length} 条数据`);
      return true;
    }
      
    return false;
  } catch (error) {
    console.error('处理批量chunk数据失败:', error);
    return false;
  }
};

/**
 * 生成图表拖拽缩略图
 * @param element - 要生成缩略图的DOM元素
 * @param x - 鼠标X坐标
 * @param y - 鼠标Y坐标
 * @param dataId - 图表数据ID，用于获取ECharts实例
 * @returns 生成的缩略图DOM元素
 */
export const createChartThumbnail = (
  element: HTMLElement,
  x: number,
  y: number,
  dataId: string,
  getItem: (id: string) => any
): HTMLDivElement => {
  const rect = element.getBoundingClientRect();
  const dragImage = document.createElement('div');
  
  // 设置缩略图容器样式
  dragImage.style.position = 'fixed';
  dragImage.style.zIndex = '10000';
  dragImage.style.pointerEvents = 'none';
  dragImage.style.opacity = '0.9';
  dragImage.style.borderRadius = '12px';
  dragImage.style.background = 'white';
  dragImage.style.transition = 'transform 0.2s';
  dragImage.style.left = `${x + 10}px`;
  dragImage.style.top = `${y + 10}px`;
  
  // 创建预览容器
  const previewContainer = document.createElement('div');
  previewContainer.style.width = `${rect.width * 0.5}px`;
  previewContainer.style.height = `${rect.height * 0.5}px`;
  previewContainer.style.overflow = 'hidden';
  
  // 克隆元素并缩放
  const clonedContent = element.cloneNode(true) as HTMLElement;
  clonedContent.style.transform = `scale(0.5)`;
  clonedContent.style.transformOrigin = 'top left';
  clonedContent.style.width = `${rect.width}px`;
  clonedContent.style.height = `${rect.height}px`;
  
  previewContainer.appendChild(clonedContent);
  
  // 在克隆的元素中查找图表容器并插入ECharts图片
  try {
    const chartContainer = clonedContent.querySelector('.dba_basic-cardbox-component-box');
    if (chartContainer) {
      const echartsInstance = getItem(dataId)?.getActiveChartInstance?.();
      if (echartsInstance) {
        const dataURL = echartsInstance.getDataURL({
          type: 'png',
          pixelRatio: 1,
          backgroundColor: '#fff'
        });
        
        // 清空容器并插入图片
        chartContainer.innerHTML = '';
        const img = document.createElement('img');
        img.src = dataURL;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'contain';
        chartContainer.appendChild(img);
      }
    }
  } catch (error) {
    console.warn('Failed to insert ECharts image into container:', error);
  }
  
  dragImage.appendChild(previewContainer);
  document.body.appendChild(dragImage);
  
  return dragImage;
};



/**
 * Agent 配置信息接口
 */
export interface IAgentConfig {
  /** 选中的 Agent ID */
  selectedAgentId: string | undefined;
  /** 当前使用的模式 */
  currentMode: string | undefined;
  /** Agent 模式映射表 */
  agentModeMap: Record<string, string>;
  /** Chat Engine */
  chatEngine: string | undefined;
}

/**
 * 获取 Agent 配置信息
 * @param options - 配置选项
 * @param options.modelId - 从服务端参数传递的模型 ID（优先级最高）
 * @param options.mode - 从服务端参数传递的模式
 * @param options.userId - 用户 ID（openid）
 * @returns Agent 配置信息
 */
export const getAgentConfig = (options: {
  modelId?: string;
  mode?: string;
  userId?: string;
}): IAgentConfig => {
  const { modelId, mode, userId } = options;

  try {
    // 1. 获取选中的 Agent ID（优先使用传入的 model_id，否则从 localStorage 读取）
    const storageKey = `${STORAGE_KEY}_${userId}__`;
    const storageValue = localStorage.getItem(storageKey);
    const selectedAgentId = modelId || (storageValue ? JSON.parse(storageValue) : undefined);

    // 2. 获取 Agent 模式映射表
    const agentModeMapKey = `__dba_agent_mode_map_${userId}__`;
    const agentModeMap = JSON.parse(localStorage.getItem(agentModeMapKey) || '{}');

    // 3. 根据 selectedAgentId 从 agentModeMap 中获取对应的 mode（优先使用传入的 mode）
    const currentMode = mode || agentModeMap[selectedAgentId];

    // 4. 获取 chatEngine（根据 selectedAgentId 查找对应的引擎）
    const chatEngine = selectedAgentId ? CHAT_ENGINE_MAP[selectedAgentId] : undefined;

    return {
      selectedAgentId,
      currentMode,
      agentModeMap,
      chatEngine,
    };
  } catch (error) {
    console.error('Failed to get agent config:', error);
    // 返回默认值
    return {
      selectedAgentId: undefined,
      currentMode: undefined,
      agentModeMap: {},
      chatEngine: undefined,
    };
  }
};

/** 取会话列表中最新一条 Query 使用的 Agent（model_id） */
export const getLatestChatModelId = (chatList: ChatList[]): string | undefined =>
  chatList?.[0]?.metadata?.model_id;
